import React, { useId } from 'react';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select } from '@mui/material';
import ErrorFormControl from 'components/forms/ErrorFormControl';
import { type IOptionalClassName } from 'types/common/Props';
import concatClassNames from 'utils/classNames';
import './Dropdown.scss';

// Keeps the label in the accessibility tree without showing it.
const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

export interface OptionType {
  id: number | string
  name: string
  disabled?: boolean
}

interface IDropdownProps extends IOptionalClassName {
  label: string
  /** Accessible name used (visually hidden) when no visible label is rendered */
  ariaLabel?: string
  options: OptionType[]
  value: number | number[] | string | string[]
  onChange: any
  disabled?: boolean
  renderValue?: (selected: any) => React.ReactNode
  error?: string
  multiple?: boolean
  onClick?: any
}

const Dropdown = ({
  label,
  ariaLabel,
  options,
  value,
  onChange,
  disabled = false,
  renderValue,
  error,
  multiple = true,
  className,
  onClick
}: IDropdownProps) => {
  const isMultiple = multiple;
  const stringValues = isMultiple ? (Array.isArray(value) ? value.map(String) : []) : [value.toString()];
  // Without this, the <InputLabel> and <Select> have no programmatic link
  // at all - screen readers can't associate the visible label with the
  // control, and anything that queries by accessible label (including
  // Playwright's getByLabel()) can never resolve it either.
  const labelId = useId();

  const handleChange = (event: any) => {
    if (multiple) {
      onChange(event);
    } else {
      onChange(event.target.value);
    }
  };

  return (
    <div className={concatClassNames('flex flex-column', className)}>
      <FormControl variant="standard" fullWidth className="dropdown">
        <InputLabel
          id={labelId}
          className="dropdown-label"
          sx={!label && ariaLabel ? visuallyHidden : undefined}
        >
          {label || ariaLabel}
        </InputLabel>
        <Select
          labelId={labelId}
          multiple={isMultiple}
          value={stringValues}
          label={label}
          onChange={handleChange}
          renderValue={renderValue}
          disabled={disabled}
          className={isMultiple ? 'dropdown-select-multiple' : 'dropdown-select'}
          onClick={onClick}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id.toString()} className="dropdown-select-item" disabled={option.disabled}>
              {isMultiple && (
                <Checkbox
                  checked={stringValues.includes(option.id.toString())}
                />
              )}
              <ListItemText primary={option.name}/>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && <ErrorFormControl error={error}/>}
    </div>
  );
};

export default Dropdown;
